import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ExportButton from '../../components/ExportButton';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown, Search, X,
  Printer, CheckCircle, XCircle, AlertCircle, AlertTriangle
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import { toast } from 'sonner';

const Printers = () => {
  const navigate = useNavigate();
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('micro', 'printers');
  
  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, sin_estado: 0 });
  
  // Data state
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', brand_id: '', status_id: '', site_id: '', location_id: '', cliente_id: '', ip_filter: '', serial_filter: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'ip_address', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  
  // Dropdowns
  const [brands, setBrands] = useState([]);
  
  // Filter options (only values that exist in printers table)
  const [filterOptions, setFilterOptions] = useState({ brands: [], statuses: [] });
  
  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(printers);
  
  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, printer: null });
  const [formData, setFormData] = useState({
    brand_id: '', empresa_id: '', site_id: '', tipo_id: '',
    ip_address: '', serial_number: '', asset_tag: '', status_id: '',
    location_id: '', supplier_id: '', cliente_id: '', comments: ''
  });
  const [dropdowns, setDropdowns] = useState({ 
    brands: [], status: [], locations: [], suppliers: [], empresas: [], sites: [], tipos: [], clientes: [] 
  });
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(25);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/printers/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPrinters = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.location_id) params.append('location_id', filters.location_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.ip_filter) params.append('ip_filter', filters.ip_filter);
      if (filters.serial_filter) params.append('serial_filter', filters.serial_filter);

      const response = await api.get(`/assets/printers?${params}`);
      setPrinters(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar impresoras');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const response = await api.get('/assets/printers/form/options');
      const data = response.data;
      setDropdowns({
        brands: data.brands || [],
        status: data.statuses || [],
        locations: data.locations || [],
        suppliers: data.suppliers || [],
        empresas: data.empresas || [],
        sites: data.sites || [],
        tipos: data.tipos || [],
        clientes: data.clientes || []
      });
      setBrands(data.brands || []);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/printers/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchDropdowns();
    fetchFilterOptions();
  }, []);

  // Fetch on page or sort change (immediate)
  useEffect(() => {
    fetchPrinters();
  }, [page, pageSize, sortConfig]);

  // Debounced filter effect - prevents focus loss when typing
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchPrinters();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand_id: '', status_id: '', site_id: '', location_id: '', cliente_id: '', ip_filter: '', serial_filter: '' });
    setActiveStatFilter('all');
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPage(1);
    if (filterType === 'all') {
      setFilters({ search: '', brand_id: '', status_id: '', site_id: '', location_id: '', cliente_id: '', ip_filter: '', serial_filter: '' });
    } else if (filterType === 'active') {
      setFilters(prev => ({ ...prev, status_id: '6' }));
    } else if (filterType === 'inactive') {
      setFilters(prev => ({ ...prev, status_id: '7' }));
    } else if (filterType === 'sin_estado') {
      setFilters(prev => ({ ...prev, status_id: 'null' }));
    }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleCreate = () => {
    setFormData({
      brand_id: '', empresa_id: '', site_id: '', tipo_id: '',
      ip_address: '', serial_number: '', asset_tag: '', status_id: '',
      location_id: '', supplier_id: '', cliente_id: '', comments: ''
    });
    setModalMode('create');
    setEditingPrinter(null);
    setCreateModal(true);
  };

  const openEditModal = (printer) => {
    if (!hasValidId(printer)) {
      toast.error('Esta impresora tiene ID nulo en la base de datos y no puede editarse individualmente. Corregí el registro (asignale un ID) para poder editarlo.');
      return;
    }
    setFormData({
      brand_id: printer.brand_id?.toString() || '',
      empresa_id: printer.empresa_id?.toString() || '',
      site_id: printer.site_id?.toString() || '',
      tipo_id: printer.tipo_id?.toString() || '',
      ip_address: printer.ip_address || '',
      serial_number: printer.serial_number || '',
      asset_tag: printer.asset_tag || '',
      status_id: printer.status_id?.toString() || '',
      location_id: printer.location_id?.toString() || '',
      supplier_id: printer.supplier_id?.toString() || '',
      cliente_id: printer.cliente_id?.toString() || '',
      comments: printer.comments || ''
    });
    setModalMode('edit');
    setEditingPrinter(printer);
    setCreateModal(true);
  };

  const openCopyModal = (printer) => {
    setFormData({
      brand_id: printer.brand_id?.toString() || '',
      empresa_id: printer.empresa_id?.toString() || '',
      site_id: printer.site_id?.toString() || '',
      tipo_id: printer.tipo_id?.toString() || '',
      ip_address: '',
      serial_number: '',
      asset_tag: '',
      status_id: printer.status_id?.toString() || '',
      location_id: printer.location_id?.toString() || '',
      supplier_id: printer.supplier_id?.toString() || '',
      cliente_id: printer.cliente_id?.toString() || '',
      comments: printer.comments || ''
    });
    setModalMode('create');
    setEditingPrinter(null);
    setCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.ip_address.trim()) {
      toast.error('La dirección IP es requerida');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        tipo_id: formData.tipo_id ? parseInt(formData.tipo_id) : null,
        ip_address: formData.ip_address.trim(),
        serial_number: formData.serial_number || null,
        asset_tag: formData.asset_tag || null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
        cliente_id: formData.cliente_id ? parseInt(formData.cliente_id) : null,
        comments: formData.comments || null
      };

      if (modalMode === 'edit' && editingPrinter) {
        await api.put(`/assets/printers/${editingPrinter.id}`, payload);
        toast.success('Impresora actualizada exitosamente');
      } else {
        await api.post('/assets/printers', payload);
        toast.success('Impresora creada exitosamente');
      }
      setCreateModal(false);
      fetchPrinters();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/printers/${deleteModal.printer.id}`);
      toast.success('Impresora eliminada');
      setDeleteModal({ open: false, printer: null });
      fetchPrinters();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // La BD de algunos clientes (ej: YEL) tiene registros con id NULL (sin AUTO_INCREMENT).
  // No se pueden ver/editar/eliminar individualmente porque la API los direcciona por id.
  const hasValidId = (p) => p?.id !== null && p?.id !== undefined;

  const openDetail = (printer) => {
    if (!hasValidId(printer)) {
      toast.error('Esta impresora tiene ID nulo en la base de datos y no puede abrirse. Asignale un ID al registro para poder verlo.');
      return;
    }
    navigate(`/micro/printers/${printer.id}`);
  };

  const requestDelete = (printer) => {
    if (!canDelete) return;
    if (!hasValidId(printer)) {
      toast.error('Esta impresora tiene ID nulo en la base de datos y no puede eliminarse individualmente.');
      return;
    }
    setDeleteModal({ open: true, printer });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Impresoras</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Impresoras</h1>
            <p className="page-subtitle">Gestion de impresoras de red</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/printers"
              filters={filters}
              filename="impresoras"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-printer-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Impresora
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="printers-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Printer className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('active')}
            data-testid="stats-active"
          >
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.active}</div>
              <div className="stats-card-label">Activas</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${activeStatFilter === 'inactive' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatFilter('inactive')}
            data-testid="stats-inactive"
          >
            <XCircle className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.inactive}</div>
              <div className="stats-card-label">Inactivas</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-amber-500/50 ${activeStatFilter === 'sin_estado' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => handleStatFilter('sin_estado')}
            data-testid="stats-sin-estado"
          >
            <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-amber-500">{stats.sin_estado}</div>
              <div className="stats-card-label">Sin Estado</div>
            </div>
          </div>
        </div>

        {/* Global Search */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th className="w-10">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isSomeSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Seleccionar todos"
                          />
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip_address')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip_address" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('brand_name')}
                        >
                          <div className="flex items-center gap-1">
                            MARCA
                            <SortIcon field="brand_name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('serial_number')}
                        >
                          <div className="flex items-center gap-1">
                            SERIE
                            <SortIcon field="serial_number" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('status_name')}
                        >
                          <div className="flex items-center gap-1">
                            ESTADO
                            <SortIcon field="status_name" />
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">CLIENTE</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('site_name')}
                        >
                          <div className="flex items-center gap-1">
                            SITE
                            <SortIcon field="site_name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('location_name')}
                        >
                          <div className="flex items-center gap-1">
                            UBICACIÓN
                            <SortIcon field="location_name" />
                          </div>
                        </th>
                        <th className="w-24 text-center">ACCIONES</th>
                      </tr>
                      {/* Filters */}
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"><Input placeholder="Filtrar..." value={filters.ip_filter} onChange={(e) => handleFilterChange('ip_filter', e.target.value)} className="h-7 text-xs bg-background" /></th>
                        <th className="py-2 px-4">
                          <Select value={filters.brand_id || undefined} onValueChange={(v) => handleFilterChange('brand_id', v === 'todos' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                            <SelectContent><SelectItem value="todos">Todos</SelectItem>{filterOptions.brands?.map(b => (<SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>))}</SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"><Input placeholder="Filtrar..." value={filters.serial_filter} onChange={(e) => handleFilterChange('serial_filter', e.target.value)} className="h-7 text-xs bg-background" /></th>
                        <th className="py-2 px-4">
                          <Select value={filters.status_id || undefined} onValueChange={(v) => handleFilterChange('status_id', v === 'todos' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                            <SelectContent><SelectItem value="todos">Todos</SelectItem>{filterOptions.statuses?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}<SelectItem value="null">Sin estado</SelectItem></SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.cliente_id || undefined} onValueChange={(v) => handleFilterChange('cliente_id', v === 'todos' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                            <SelectContent><SelectItem value="todos">Todos</SelectItem>{dropdowns.clientes?.map(c => (<SelectItem key={c.id} value={String(c.id)}>{c.nombre || c.name}</SelectItem>))}</SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.site_id || undefined} onValueChange={(v) => handleFilterChange('site_id', v === 'todos' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                            <SelectContent><SelectItem value="todos">Todos</SelectItem>{dropdowns.sites?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.site || s.name}</SelectItem>))}</SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select value={filters.location_id || undefined} onValueChange={(v) => handleFilterChange('location_id', v === 'todos' ? '' : v)}>
                            <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                            <SelectContent><SelectItem value="todos">Todos</SelectItem>{dropdowns.locations?.map(l => (<SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>))}</SelectContent>
                          </Select>
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {printers.length === 0 ? (
                        <tr>
                          <td colSpan={9}>
                            <div className="empty-state">
                              <Printer className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron impresoras</p>
                            </div>
                          </td>
                        </tr>
                      ) : printers.map((printer, idx) => (
                        <tr key={printer.id ?? `row-${idx}`} data-testid={`printer-row-${printer.id ?? `null-${idx}`}`} className={isSelected(printer.id) ? 'bg-primary/5' : (!hasValidId(printer) ? 'bg-amber-500/5' : '')}>
                          <td className="w-10">
                            <Checkbox
                              checked={isSelected(printer.id)}
                              onCheckedChange={() => toggleRow(printer.id)}
                              aria-label={`Seleccionar ${printer.ip_address}`}
                            />
                          </td>
                          <td 
                            className={`font-mono ${hasValidId(printer) ? 'text-primary cursor-pointer hover:underline' : 'text-foreground'}`}
                            onClick={() => openDetail(printer)}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              {!hasValidId(printer) && (
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" title="Registro sin ID en la base de datos (id nulo). No puede abrirse ni editarse individualmente." />
                              )}
                              {printer.ip_address}
                            </span>
                          </td>
                          <td>{printer.brand_name || <span className="text-muted-foreground">-</span>}</td>
                          <td className="font-mono text-sm">{printer.serial_number || <span className="text-muted-foreground">-</span>}</td>
                          <td>
                            {printer.status_name ? (
                              <span className={`badge ${printer.status_name === 'Activo' ? 'badge-green' : 'badge-red'}`}>
                                {printer.status_name}
                              </span>
                            ) : (
                              <span className="badge badge-amber">Sin estado</span>
                            )}
                          </td>
                          <td className="text-muted-foreground">{printer.cliente_name || '-'}</td>
                          <td className="text-muted-foreground">{printer.site_name || '-'}</td>
                          <td className="text-muted-foreground">{printer.location_name || '-'}</td>
                          <td className="w-24">
                            {!hasValidId(printer) ? (
                              <div className="flex justify-center">
                                <span
                                  className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500"
                                  title="Registro sin ID en la base de datos (id nulo). No puede abrirse, editarse ni eliminarse individualmente. Corregí el registro asignándole un ID."
                                  data-testid={`printer-noid-warning-${idx}`}
                                >
                                  <AlertTriangle className="h-4 w-4" />
                                  Sin ID
                                </span>
                              </div>
                            ) : (
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => openDetail(printer)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-printer-${printer.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {canUpdate && (
                                <button
                                  onClick={() => openEditModal(printer)}
                                  className="action-btn"
                                  title="Editar"
                                  data-testid={`edit-printer-${printer.id}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                              {canCreate && (
                                <button
                                  onClick={() => openCopyModal(printer)}
                                  className="action-btn"
                                  title="Copiar"
                                  data-testid={`copy-printer-${printer.id}`}
                                >
                                  <Copy className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => requestDelete(printer)}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-printer-${printer.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages || 1}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages || totalPages === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={createModal} onOpenChange={setCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Printer className="h-5 w-5 text-primary" />
                {modalMode === 'edit' ? 'Editar Impresora' : 'Nueva Impresora'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Direccion IP *</Label>
                  <Input
                    value={formData.ip_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                    placeholder="192.168.1.100"
                    data-testid="input-ip"
                    data-validation={modalMode === 'create' && !formData.ip_address ? 'required-empty' : ''}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Numero de Serie</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                    placeholder="ABC123"
                    data-validation={modalMode === 'create' && !formData.serial_number ? 'required-empty' : ''}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Asset Tag</Label>
                  <Input
                    value={formData.asset_tag}
                    onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                    placeholder="IMP-001"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <select
                    value={formData.tipo_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, tipo_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.tipo_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.tipos.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Marca</Label>
                  <select
                    value={formData.brand_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, brand_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.brand_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.brands.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <select
                    value={formData.status_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, status_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.status_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.status.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Empresa</Label>
                  <select
                    value={formData.empresa_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, empresa_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.empresa_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.empresas.map(e => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Site</Label>
                  <select
                    value={formData.site_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, site_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.site_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.sites.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Ubicacion</Label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, location_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.location_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.locations.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Proveedor</Label>
                  <select
                    value={formData.supplier_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, supplier_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.supplier_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.suppliers.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cliente</Label>
                  <select
                    value={formData.cliente_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, cliente_id: e.target.value }))}
                    className={`h-9 w-full text-sm bg-background border rounded px-2 ${modalMode === 'create' && formData.cliente_id ? 'border-green-500' : 'border-border'}`}
                  >
                    <option value="">Seleccionar...</option>
                    {dropdowns.clientes?.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Comentarios</Label>
                <Input
                  value={formData.comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Comentarios adicionales..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, printer: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Impresora
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Estas seguro que deseas eliminar la impresora <strong>{deleteModal.printer?.ip_address}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, printer: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchPrinters(); fetchStats(); }}
          endpoint="/assets/printers"
          options={{
            sites: dropdowns.sites?.map(s => ({ id: s.id, nombre: s.site || s.name })),
            empresas: dropdowns.empresas?.map(e => ({ id: e.id, nombre: e.name })),
            statuses: dropdowns.status?.map(s => ({ id: s.id, nombre: s.name })),
            ubicaciones: dropdowns.locations?.map(l => ({ id: l.id, nombre: l.name })),
            brands: dropdowns.brands?.map(b => ({ id: b.id, nombre: b.name }))
          }}
          fields={['site_id', 'empresa_id', 'status_id', 'location_id', 'brand_id']}
        />
      </div>
    </Layout>
  );
};

export default Printers;
